import { ComponentProps, FC, ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from "react"

import { ArrowPathIcon, DocumentDuplicateIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"

import { ConfirmModal } from "../AdaKit/Modal"
import { Button } from "../BeeKit/Button"
import { Toggle } from "../BeeKit/Toggle"
import PriceQuantitiesTable from "./PriceQuantitiesTable"
import { FormPriceQuantity } from "./PriceQuantityFields"
import { SectionHeader } from "./SectionHeader"
import { SetupPricingTable } from "./SetupPricingTable"
import ThirdPartyDecorationSelector from "./ThirdPartyDecorationSelector"
import { isNil, Nil } from "~/src/lib/any"
import { PathBuilder } from "~/src/lib/iname"
import { PriceGroup } from "~/src/serializedRecords"
import { PriceGroupDiscountCode } from "~/src/serializedRecords/priceGroupDiscountCode"

export type FormPriceGroup = Omit<PriceGroup, "priceQuantities"> & {
  priceQuantities: FormPriceQuantity[]
  _edited?: boolean
  _destroy?: boolean
}

type PriceGroupFieldsProps = {
  minimumMargin: number
  group: FormPriceGroup
  baseIname: PathBuilder
  discountCodes: PriceGroupDiscountCode[]
  onDeletePriceGroup: () => void
  onDuplicatePriceGroup: () => void
  onToggleDefault: () => void
}

const useEditStatus = (editStatus: boolean) => {
  const [edited, setEditStatus] = useState(editStatus)

  useEffect(() => {
    if (edited != editStatus) {
      setEditStatus(editStatus)
    }
  }, [editStatus])

  return [edited, setEditStatus] as const
}

export const PriceGroupFields: FC<PriceGroupFieldsProps> = ({
  minimumMargin,
  group,
  baseIname,
  discountCodes,
  onDeletePriceGroup,
  onDuplicatePriceGroup,
  onToggleDefault,
}) => {
  const [edited, setEditStatus] = useEditStatus(!!group._edited)
  const [decorationPriceGroups, setDecorationPriceGroups] = useState(
    group.decorationPriceGroups.length > 0 ? group.decorationPriceGroups : [{ decorationId: undefined, name: "" }]
  )
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedDiscountCode, setSelectedDiscountCode] = useState<PriceGroupDiscountCode>(() => {
    const groupDiscountCode = discountCodes.find((code) => group.setupDiscount === code.discount)

    return groupDiscountCode || discountCodes.find((code) => code.discount === 0) || discountCodes[0]
  })
  const [setupPrice, setSetupPrice] = useState(group.setupPrice || 0)
  const netSetup = selectedDiscountCode ? setupPrice * (1 - selectedDiscountCode.discount) : setupPrice

  // We use the discount as a starting point for what our margin is. We want a minimum margin of the global margin
  // though. If the discount is less, lock it to the global margin.
  const margin =
    selectedDiscountCode && selectedDiscountCode.discount < minimumMargin
      ? minimumMargin
      : selectedDiscountCode.discount
  const markedUpNetSetup = netSetup / (1 - margin)
  const netSetupValue = isNaN(netSetup) ? 0 : netSetup
  const markedUpNetSetupValue = isNaN(markedUpNetSetup) ? 0 : markedUpNetSetup

  const handleAddGroup = () => {
    setDecorationPriceGroups([...decorationPriceGroups, { decorationId: undefined, name: "" }])
  }

  const handleRemoveGroup = (index: number) => {
    setDecorationPriceGroups((prevGroups) =>
      prevGroups.map((group, i) => (i === index ? { ...group, _destroy: "1" } : group))
    )
  }

  const handleChangeGroup = (index: number, selectedOption: { value: number; label: string } | null) => {
    if (selectedOption) {
      const updatedGroups = [...decorationPriceGroups]
      updatedGroups[index] = { decorationId: selectedOption.value, name: selectedOption.label }
      setDecorationPriceGroups(updatedGroups)
    }
  }

  const handleConfirmDelete = useCallback(() => {
    setDeleteModalOpen(false)
    onDeletePriceGroup()
  }, [onDeletePriceGroup])

  const confirmDeleteText = useMemo(() => {
    if (group.id) {
      return (
        "Deleting this price group will hide it from future lookbooks but won't impact any orders it has been " +
        "attached to previously. This action cannot be undone."
      )
    } else {
      return "Are you sure you want to delete this price group? This action cannot be undone."
    }
  }, [group.id])

  return edited ? (
    <div className="mb-4 flex flex-col gap-10 rounded-md border border-gray-200 pt-6 pr-4 pb-12 pl-4">
      <section>
        <SectionHeader title="Price Group">
          <IconButton
            icon={<DocumentDuplicateIcon className="size-4" />}
            text="Duplicate Group"
            onClick={onDuplicatePriceGroup}
          />
          <IconButton
            icon={<TrashIcon className="size-4" />}
            text="Delete Group"
            className="text-red-600"
            onClick={() => setDeleteModalOpen(true)}
          />
          <ConfirmModal
            title="Delete price group?"
            okBtn={{ kind: "danger", text: "Delete" }}
            open={isDeleteModalOpen}
            onAccept={handleConfirmDelete}
            onClose={() => setDeleteModalOpen(false)}
          >
            {confirmDeleteText}
          </ConfirmModal>
        </SectionHeader>
        <div className="flex flex-col gap-6">
          <label className="mb-2 flex flex-row items-start justify-start gap-4">
            <div className="flex h-11 w-40 items-center text-sm leading-tight font-medium text-gray-900">
              <span className="block">Decoration method</span>
            </div>
            <div className="flex flex-auto flex-col items-start justify-center gap-2">
              <input
                type="text"
                name={baseIname.name.toString()}
                defaultValue={group.name}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <p className="mt-1 text-sm text-gray-500">This name is visible to the client</p>
            </div>
          </label>

          <ThirdPartyDecorationSelector
            baseIname={baseIname}
            decorationPriceGroups={decorationPriceGroups}
            handleAddGroup={handleAddGroup}
            handleRemoveGroup={handleRemoveGroup}
            handleChange={handleChangeGroup}
          />

          <div className="flex items-center space-x-6">
            <div className="flex items-center gap-2 space-x-2">
              <Toggle
                id={baseIname.default.toString()}
                name={baseIname.default.toString()}
                defaultChecked={group.default}
                key={group.default ? "checked" : "unchecked"} // Add a dynamic key to force re-render
                onChange={onToggleDefault}
              />
              <label htmlFor={baseIname.default.toString()} className="text-sm text-gray-900">
                Set as default price group
              </label>
            </div>
          </div>
        </div>
      </section>

      <section>
        <SectionHeader title="Setup price" />
        <SetupPricingTable
          baseIname={baseIname}
          discountCodes={discountCodes}
          margin={margin}
          netSetupCost={netSetupValue}
          netSetupPrice={markedUpNetSetupValue}
          selectedDiscountCode={selectedDiscountCode}
          setupPrice={setupPrice}
          onDiscountCodeChange={setSelectedDiscountCode}
          onSetupPriceChange={setSetupPrice}
        />
      </section>

      <section className="mb-4">
        <SectionHeader title="Unit price" />
        <PriceQuantitiesTable
          baseIname={baseIname.priceQuantitiesAttributes}
          minimumMargin={minimumMargin}
          group={group}
          priceGroupDiscountCodes={discountCodes}
          netSetupPrice={markedUpNetSetupValue}
        />
      </section>

      {!group.id ? null : <input type="hidden" name={baseIname.id.toString()} value={group.id} />}
    </div>
  ) : (
    <ReadOnlyPriceGroup
      group={group}
      priceGroupDiscountCodes={discountCodes}
      onEdit={() => setEditStatus(true)}
      margin={margin}
      minimumMargin={minimumMargin}
      netSetupCost={netSetupValue}
      netSetupPrice={markedUpNetSetupValue}
      discountCode={selectedDiscountCode.label}
      setupPrice={setupPrice}
      confirmDeleteText={confirmDeleteText}
      handleConfirmDelete={handleConfirmDelete}
      setDeleteModalOpen={setDeleteModalOpen}
      onDuplicatePriceGroup={onDuplicatePriceGroup}
      isDeleteModalOpen={isDeleteModalOpen}
    />
  )
}

const ReadOnlyPriceGroup = ({
  onEdit,
  group,
  margin,
  minimumMargin,
  netSetupCost,
  netSetupPrice,
  discountCode,
  setupPrice,
  onDuplicatePriceGroup,
  setDeleteModalOpen,
  handleConfirmDelete,
  isDeleteModalOpen,
  confirmDeleteText,
  priceGroupDiscountCodes,
}: {
  margin: number
  minimumMargin: number
  netSetupCost: number
  netSetupPrice: number
  discountCode: string
  setupPrice: number
  group: FormPriceGroup
  onEdit: () => void
  onDuplicatePriceGroup: () => void
  setDeleteModalOpen: (s: boolean) => void
  handleConfirmDelete: () => void
  isDeleteModalOpen: boolean
  confirmDeleteText: string
  priceGroupDiscountCodes: PriceGroupDiscountCode[]
}) => {
  return (
    <div className="rounded-lg border bg-white p-6">
      <SectionHeader title="Price Group">
        <IconButton icon={<PencilIcon className="size-4" />} text="Edit" onClick={onEdit} />
        <IconButton
          icon={<DocumentDuplicateIcon className="size-4" />}
          text="Duplicate Group"
          onClick={onDuplicatePriceGroup}
        />
        <IconButton
          icon={<TrashIcon className="size-4" />}
          text="Delete Group"
          className="text-red-600"
          onClick={() => setDeleteModalOpen(true)}
        />
        <ConfirmModal
          title="Delete price group?"
          okBtn={{ kind: "danger", text: "Delete" }}
          open={isDeleteModalOpen}
          onAccept={handleConfirmDelete}
          onClose={() => setDeleteModalOpen(false)}
        >
          {confirmDeleteText}
        </ConfirmModal>
      </SectionHeader>
      <section className="text-grey-900 mb-8 text-sm">
        <div className="with-sidebar">
          <span className="basis-40 font-medium">Decoration method</span>
          <span>{group.name}</span>
        </div>

        <div className="with-sidebar">
          <span className="basis-40 font-medium">Third-party decoration</span>
          <span>
            {group.decorationPriceGroups.length
              ? group.decorationPriceGroups.map((linkedGroup) => linkedGroup.name).join(", ")
              : "None"}
          </span>
        </div>
        <div className="with-sidebar">
          <span className="text-md basis-40 font-medium">Default price group</span>
          <span>{group.default ? "Yes" : "No"}</span>
        </div>
      </section>

      <div className="mb-8">
        <SectionHeader title="Setup price" />
        <ReadOnlySetupPricingTable
          margin={margin}
          discountCode={discountCode}
          setupPrice={setupPrice}
          netSetupCost={netSetupCost}
          netSetupPrice={netSetupPrice}
        />
      </div>

      <div className="flex flex-col">
        <SectionHeader title="Unit price" />
        <ReadOnlyPriceQuantitiesTable
          minimumMargin={minimumMargin}
          netSetupPrice={netSetupPrice}
          priceQuantities={group.priceQuantities}
          priceGroupDiscountCodes={priceGroupDiscountCodes}
        />
      </div>
    </div>
  )
}

const headers = ["Setup Price", "Discount Code", "Net Setup Cost", "Margin", "Net Setup Price"]
function ReadOnlySetupPricingTable({
  netSetupCost,
  netSetupPrice,
  discountCode,
  setupPrice,
  margin,
}: {
  margin: number
  netSetupCost: number
  netSetupPrice: number
  setupPrice: number
  discountCode?: string
}) {
  return (
    <table className="mb-0 w-auto border-separate border-spacing-0 text-sm">
      <thead>
        <tr className="h-14 bg-gray-100 px-4">
          {headers.map((header, index) => (
            <th
              key={index}
              className="border-t border-b border-l border-gray-300 px-4 py-2 text-left align-middle font-medium first:rounded-tl-lg last:rounded-tr-lg last:border-r"
            >
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr className="group/row">
          <td className="border-b border-l px-4 py-2 text-left align-middle group-last/row:rounded-bl-lg">
            ${setupPrice.toFixed(2)}
          </td>
          <td className="border-b border-l px-4 py-2 text-left align-middle">{discountCode ?? ""}</td>
          <td className="border-b border-l px-4 py-2 text-left align-middle">${netSetupCost.toFixed(2)}</td>
          <td className="border-b border-l px-4 py-2 text-left align-middle">{(margin * 100).toFixed(0)}%</td>
          <td className="border-r border-b border-l px-4 py-2 text-left align-middle font-medium group-last/row:rounded-br-lg">
            ${netSetupPrice.toFixed(2)}
          </td>
        </tr>
      </tbody>
    </table>
  )
}

type HeaderDefinition = {
  header: string | ReactNode
  headerClass: string
}

type HeaderGroup = HeaderDefinition[]

const headerGroups: HeaderGroup[] = [
  [{ header: "Low quantity", headerClass: "text-left" }],
  [
    { header: "Unit price", headerClass: "text-left" },
    { header: "Discount code", headerClass: "text-left" },
    { header: "Net unit cost", headerClass: "text-left" },
  ],
  [
    { header: "Run charge", headerClass: "text-left" },
    { header: "Discount code", headerClass: "text-left" },
    { header: "Net run charge", headerClass: "text-left" },
  ],
  [
    { header: "Profit margin", headerClass: "text-left" },
    {
      header: "Third-party deco",
      headerClass: "text-left",
    },
    { header: "Display price", headerClass: "text-left" },
  ],
]

function ReadOnlyPriceQuantitiesTable({
  priceQuantities,
  priceGroupDiscountCodes,
  netSetupPrice,
  minimumMargin,
}: {
  priceQuantities: Array<FormPriceQuantity>
  priceGroupDiscountCodes: Array<PriceGroupDiscountCode>
  netSetupPrice: number
  minimumMargin: number
}) {
  return (
    <table className="w-auto max-w-full border-separate border-spacing-0 text-sm">
      <thead>
        <tr className="h-14 bg-gray-100 px-4">
          {headerGroups.flatMap((headers, groupIndex) =>
            headers.map(({ header, headerClass }, headerIndex) => {
              const className = twMerge(
                "border-y border-gray-300 px-4 py-2 align-middle font-medium first:rounded-tl-lg last:rounded-tr-lg last:border-r",
                headerIndex === 0 && "border-l",
                headerClass
              )
              return (
                <th key={`${groupIndex}-${headerIndex}`} className={className}>
                  {header}
                </th>
              )
            })
          )}
        </tr>
      </thead>
      <tbody>
        {priceQuantities.map((row, index) => {
          const discountCode = priceGroupDiscountCodes.find((c) => c.discount === row.discount)
          const netUnitCost = row.supplierPrice * (1 - row.discount)
          const netRunChargeCost = row.runChargePrice * (1 - row.runChargeDiscount)
          const netUnitSetupPrice = netSetupPrice / row.minQuantityThreshold

          const defaultMargin = row.discount < minimumMargin ? minimumMargin : row.discount
          const margin = row.profitMarginPct ? row.profitMarginPct * 0.01 : defaultMargin

          const decorationPrice = row.decorationPrice

          const netUnitPrice = isNil(decorationPrice)
            ? null
            : (netUnitCost + netRunChargeCost) / (1 - margin) + decorationPrice + netUnitSetupPrice

          const lastRow = index == priceQuantities.length - 1
          return (
            <tr key={index} className="border border-gray-300">
              <td
                className={twMerge(
                  "border-b border-l border-gray-300 px-4 py-2 text-left align-middle",
                  lastRow && "rounded-bl-lg"
                )}
              >
                {row.minQuantityThreshold}
              </td>
              <td className="border-b border-l border-gray-300 px-4 py-2 text-left align-middle">
                ${row.supplierPrice}
              </td>
              <td className="border-b border-gray-300 px-4 py-2 text-left align-middle">{discountCode?.label}</td>
              <td className="border-b border-gray-300 px-4 py-2 text-left align-middle">${netUnitCost.toFixed(2)}</td>
              <td className="border-b border-l border-gray-300 px-4 py-2 text-left align-middle">
                ${row.runChargePrice}
              </td>
              <td className="border-b border-gray-300 px-4 py-2 text-left align-middle">{row.runChargeDiscount}</td>
              <td className="border-b border-gray-300 px-4 py-2 text-left align-middle">
                ${netRunChargeCost.toFixed(2)}
              </td>
              <td className="border-b border-l border-gray-300 px-4 py-2 text-left align-middle">
                {(margin * 100).toFixed(0)}%
              </td>
              <td className="border-b border-gray-300 px-4 py-2 text-left align-middle">
                {nullableMoney(decorationPrice)}
              </td>
              <td
                className={twMerge(
                  "border-r border-b border-gray-300 px-6 py-2 text-left align-middle",
                  lastRow && "rounded-br-lg"
                )}
              >
                <span className="inline-block min-w-20 rounded-md bg-slate-200 px-2 text-center">
                  {nullableMoney(netUnitPrice)}
                </span>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export type IconButtonProps = {
  icon: ReactElement
  text: string
  className?: string | undefined
} & ComponentProps<"button">

/**
 * IconButton component, a generic button with an icon and text.
 * Uses a Button component under the hood with forwardRef to propagate props.
 */
export function IconButton({ icon, text, className = "text-gray-900", ...restProps }: IconButtonProps) {
  const hoverClass = className.includes("text-red") ? "hover:bg-red-100" : "hover:bg-gray-100"
  return (
    <Button
      className={twMerge(
        `flex items-center space-x-2 border-gray-200 bg-white px-3 py-2 text-sm font-medium shadow-none ${hoverClass}`,
        className
      )}
      {...restProps}
    >
      {icon}
      <span>{text}</span>
    </Button>
  )
}
IconButton.displayName = "IconButton"

const nullableMoney = (num: number | Nil) =>
  isNil(num) ? <ArrowPathIcon className="inline-block size-4" /> : `$${num.toFixed(2)}`
